import { useEffect, useRef } from 'react'

type RefHook<T> = {
  current: T
}

export const useComparatorRef = <T>(
  value: T | null | undefined,
  isEqual: (
    v1: T | null | undefined,
    v2: T | null | undefined
  ) => boolean,
  onChange: (value: T | null | undefined, previous: T | null | undefined) => void,
  initialChange?: boolean
): RefHook<T | null | undefined> => {
  const mountedRef = useRef(true)
  const ref = useRef(initialChange ? null : value)

  useEffect(() => {
    // If initial change is needed then reset the value after unmounting
    if (!mountedRef.current && initialChange) {
      ref.current = null
      mountedRef.current = true
    }
    // Use comparator to see if the value has changed
    if (!isEqual(value, ref.current)) {
      const previous = ref.current
      ref.current = value
      onChange(ref.current, previous)
    }
  }, [initialChange, isEqual, onChange, value])

  // Effect with no dependencies to track component mount state
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  return ref
}

export const useIdentifyRef = <T>(
  value: T | null | undefined,
  onChange: () => void
): RefHook<T | null | undefined> => {
  return useComparatorRef(value, (v1, v2) => v1 === v2, onChange)
}

export const useObjectIsRef = <T>(
  value: T | null | undefined,
  onChange: () => void
): RefHook<T | null | undefined> => {
  return useComparatorRef(value, Object.is, onChange)
}

export const useArrayObjectIsRef = <T>(
  value: T[] | null | undefined,
  onChange: (value: T[] | null | undefined, previous: T[] | null | undefined) => void,
  initialChange?: boolean
): RefHook<T[] | null | undefined> => {
  return useComparatorRef(value, (v1, v2) => {
    if (Object.is(v1, v2)) return true
    if (v1 && v2) {
      return (
        v1.length === v2.length &&
        v1.every((item, index) => Object.is(item, v2[index]))
      )
    }
    return false
  }, onChange, initialChange)
}
